import { handleCustomDoorAttributeCellChanged } from "components/allocation-dimension";
import { validateDoorAttributesData } from "helpers/validators/import-door-groupings-validator";
import { ALLOCATION_BAG_ID } from "constants/product-collection";
import {
    IAllocationDimension,
    IAllocationDimensionValue,
    generateAllocationDimensionAttribute,
} from "helpers/allocation-dimension";
import ExcelManager from "helpers/excel-manager";
import FileManager from "helpers/file-manager";
import { amphibiousGetIn } from "helpers/objUtils";
import { ISchemaKey } from "helpers/product-collection";
import React, { FC, useCallback, useMemo, useReducer, useState } from "react";
import { connect } from "react-redux";
import { IStore } from "reducers";

import * as Sentry from "@sentry/browser"; // error tacking
import {
    DoorsForDisableOrEnable,
    DoorsValueGroupedById,
    HandleProcessedSuccessfullyProps,
    IAttributeData,
    IAttributeDataError,
    RuleType,
    UnitByDoor,
} from "../../types";
import {
    getFieldNameToCustomValueList,
    getDoorsAttributesDataChanges,
    removeRawColumns,
    updateDoorAttributesDataWithAlternativeId,
} from "../../utils";
import { UploadActionKind } from "./upload-actions";
import { defaultUploadProviderStateReducer, uploadReducer } from "./upload-reducer";
import { UploaderContext } from "./uploader-context";
import { EmptyHeaderNameExpression } from "../../constants";
import { IMerchantSharedSettings, MAX_USER_CUSTOM_ATTRIBUTES } from "constants/merchant";

interface IReduxProps {
    merchantSharedSettings: IMerchantSharedSettings;
    mutate: any;
}

interface IUploaderProviderProps {
    doorsDimension: IAllocationDimension;
    onCustomDoorCellValueChanged?: (
        attributes: ISchemaKey[],
        allocationDimensionId: string,
        rowIndexToDataMap: any
    ) => void;
    handleToggleImportDoorAttributesDialog: () => void;
    handleToggleManageAllocationDimension: () => void;
}

export const BaseUploaderProvider: FC<IUploaderProviderProps & IReduxProps> = ({
    children,
    doorsDimension,
    onCustomDoorCellValueChanged,
    mutate,
    handleToggleImportDoorAttributesDialog,
    handleToggleManageAllocationDimension,
    merchantSharedSettings,
}) => {
    const { custom_attributes_limit } = merchantSharedSettings;
    const customAttributesLimit = custom_attributes_limit || MAX_USER_CUSTOM_ATTRIBUTES;

    const [{ changes, errors, feedbackUpdatingData, feedbackUpload }, dispatch] = useReducer(
        uploadReducer,
        defaultUploadProviderStateReducer
    );

    const [isOpenDialogToConfirmChanges, setIsOpenDialogToConfirmChanges] = useState<boolean>(false);

    const [rowIndexToDataMap, setRowIndexToDataMap] = useState<Map<number, any>>(new Map<number, any>());

    const [totalUnitsByDoorCodeId, setTotalUnitsByDoorCodeId] = useState<UnitByDoor>({});

    const [sanitizedDataLocalState, setSanitizedDataLocalState] = useState<any[]>([]);

    let idSetInterval;

    const handleErrorWhileProcessing = (errors: IAttributeDataError) => {
        dispatch({ type: UploadActionKind.AddErrorMessages, payload: errors });
    };

    const handleAddRowToUpdate = useCallback(
        (rowIndex: number, row: IAllocationDimensionValue) => {
            setRowIndexToDataMap(rowIndexToDataMap.set(rowIndex, row));
        },
        [rowIndexToDataMap]
    );

    const handleClearValueStates = () => {
        setRowIndexToDataMap(new Map<number, any>());

        dispatch({ type: UploadActionKind.resetAllStates });
    };

    const doorsValueGroupedByCodeId = useMemo<DoorsValueGroupedById>(() => {
        return doorsDimension.dimension_values?.reduce((acc, dimensionValue, currentIndex) => {
            acc[dimensionValue.external_id] = {
                row: dimensionValue,
                rowIndex: currentIndex,
            };
            return acc;
        }, {});
    }, [doorsDimension.dimension_values]);

    const handleProcessedSuccessfully = useCallback(
        ({ doorAttributesData, readOnlyAttributes }: HandleProcessedSuccessfullyProps) => {
            const {
                columnsToUpdate,
                columnsToCreate,
                columnsHeadersMap,
                doorsForDisableOrEnable,
                doorsForDisableOrEnableWithUnits,
            } = getDoorsAttributesDataChanges({
                doorAttributesData,
                readOnlyAttributes,
                totalUnitsByDoorCodeId,
                existingDoorAttributes: doorsDimension.attributes.keys,
                doorsValueGroupedByCodeId,
                onSetSanitizedAttributesData: setSanitizedDataLocalState,
            });

            !!columnsHeadersMap.size &&
                dispatch({ type: UploadActionKind.SetColumnHeaders, payload: columnsHeadersMap });
            !!columnsToUpdate.length &&
                dispatch({ type: UploadActionKind.AddColumnsToUpdate, payload: columnsToUpdate });
            !!columnsToCreate.length &&
                dispatch({ type: UploadActionKind.AddColumnsToCreate, payload: columnsToCreate });
            !!doorsForDisableOrEnable.length &&
                dispatch({ type: UploadActionKind.AddDoorsForEnableOrDisable, payload: doorsForDisableOrEnable });
            !!doorsForDisableOrEnableWithUnits.length &&
                dispatch({
                    type: UploadActionKind.AddDoorsForEnableOrDisableWithUnits,
                    payload: doorsForDisableOrEnableWithUnits,
                });
        },
        [doorsDimension, totalUnitsByDoorCodeId, doorsValueGroupedByCodeId]
    );

    const handleDrop = useCallback(
        async (file: File, onSettled: (error: boolean) => void) => {
            handleClearValueStates();
            dispatch({ type: UploadActionKind.IsUploadingFile });

            try {
                const result = await FileManager.readAsArrayBuffer(file);
                const doorAttributesData: IAttributeData[] = removeRawColumns(
                    updateDoorAttributesDataWithAlternativeId(ExcelManager.read(result)),
                    [EmptyHeaderNameExpression]
                );

                const {
                    [RuleType.AttributeAlreadyExistsRule]: attributesAlreadyExists,
                    [RuleType.AttributeIsReadonlyRule]: readOnlyAttributes,
                    ...errors
                } = validateDoorAttributesData({
                    data: doorAttributesData,
                    allocationDimension: doorsDimension,
                    customAttributesLimit,
                });

                const hasErrors = Object.keys(errors || {}).length > 0;
                onSettled(hasErrors);
                if (hasErrors) {
                    handleErrorWhileProcessing(errors);
                    return;
                }
                handleProcessedSuccessfully({ doorAttributesData, readOnlyAttributes });
                dispatch({ type: UploadActionKind.ClearErrorMessages });
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(error);
            } finally {
                dispatch({ type: UploadActionKind.FileProcessed });
            }
        },

        [customAttributesLimit, doorsDimension, handleProcessedSuccessfully]
    );

    const handleGetDoorDimensionWithDisableOrEnableDoors = useCallback(
        (doorsName: DoorsForDisableOrEnable[], updatedDoorDimension: IAllocationDimension) => {
            if (!doorsName.length) {
                return updatedDoorDimension;
            }

            const newRowsWithDoorsEnabledOrDisabled = [...doorsDimension.dimension_values];

            doorsName.forEach(({ id, in_assortment }) => {
                const doorGroupedById = doorsValueGroupedByCodeId[id];

                if (doorGroupedById?.row) {
                    const { row, rowIndex } = doorsValueGroupedByCodeId[id];
                    const rowExisting = rowIndexToDataMap.get(rowIndex);

                    let newRow;

                    if (rowExisting) {
                        newRow = {
                            ...rowExisting,
                            included: in_assortment,
                        };
                    } else {
                        newRow = {
                            ...row,
                            included: in_assortment,
                        };
                    }

                    handleAddRowToUpdate(rowIndex, newRow);
                    newRowsWithDoorsEnabledOrDisabled.splice(rowIndex, 1, newRow);
                }
            });

            return {
                ...updatedDoorDimension,
                dimension_values: newRowsWithDoorsEnabledOrDisabled,
            };
        },
        [handleAddRowToUpdate, doorsDimension, doorsValueGroupedByCodeId, rowIndexToDataMap]
    );

    const handleGetUpdatedDoorAttributes = useCallback(
        (customAttributesName: string[], columnsHeadersMap: Map<string, string>) => {
            const doorAttributes = [...doorsDimension.attributes.keys];

            if (!customAttributesName.length) {
                return doorAttributes;
            }

            customAttributesName.forEach((customAttributeName) => {
                doorAttributes.push(generateAllocationDimensionAttribute(columnsHeadersMap.get(customAttributeName)));
            });

            return doorAttributes;
        },
        [doorsDimension]
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleConfirmChanges = () => {
        try {
            dispatch({ type: UploadActionKind.IsUpdatingData });

            const updatedDoorAttributes = handleGetUpdatedDoorAttributes(
                changes.columnsToCreate,
                changes.columnsHeadersMap
            );
            const updatedDoorDimension = {
                ...doorsDimension,
                attributes: {
                    ...doorsDimension.attributes,
                    keys: updatedDoorAttributes,
                },
            };
            const fieldNameToCustomValueList = getFieldNameToCustomValueList({
                sanitizedAttributesData: sanitizedDataLocalState,
                doorsValueGroupedByCodeId,
                dimensionValues: doorsDimension.dimension_values,
                onAddRowToUpdate: handleAddRowToUpdate,
            });
            const updatedDoorDimensionWithDoorsEnabledOrDisabled = handleGetDoorDimensionWithDisableOrEnableDoors(
                changes.doorsForDisableOrEnable,
                updatedDoorDimension
            );

            handleCustomDoorAttributeCellChanged({
                fieldNameToCustomValueList,
                rowIndexToDataMap,
                isPasteOp: false,
                attributes: updatedDoorDimensionWithDoorsEnabledOrDisabled.attributes.keys,
                allocationDimensionId: updatedDoorDimensionWithDoorsEnabledOrDisabled.id,
                onCustomDoorCellValueChanged,
            });
        } catch (error) {
            Sentry.captureException(error);
        } finally {
            // eslint-disable-next-line react-hooks/exhaustive-deps
            idSetInterval = setInterval(() => {
                dispatch({ type: UploadActionKind.DataUpdated });
                clearInterval(idSetInterval);
            }, 500);
        }
    };

    const bagValues = mutate.snapshots.get(ALLOCATION_BAG_ID);

    React.useEffect(() => {
        if (bagValues) {
            bagValues?.forEach((currentItem) => {
                const fields = amphibiousGetIn(currentItem, ["map", "fields"]);

                fields?.forEach((value, key) => {
                    const doorIdWithoutDeliveryId = key.split("#")[1];
                    const codeId = doorsDimension.dimension_values.find(
                        (value) => value.id === doorIdWithoutDeliveryId
                    )?.external_id;

                    if (!totalUnitsByDoorCodeId[codeId]) {
                        setTotalUnitsByDoorCodeId((prevState) => ({
                            ...prevState,
                            [codeId]: Number(amphibiousGetIn(value, ["string_value"])),
                        }));
                    } else {
                        setTotalUnitsByDoorCodeId((prevState) => ({
                            ...prevState,
                            [codeId]: prevState[codeId] + Number(amphibiousGetIn(value, ["string_value"])),
                        }));
                    }
                });
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bagValues]);

    const doorsForEnableWithUnits = useMemo(
        () => changes.doorsForDisableOrEnableWithUnits.filter((door) => door.in_assortment).map(({ name }) => name),
        [changes.doorsForDisableOrEnableWithUnits]
    );
    const doorsForDisableWithUnits = useMemo(
        () =>
            changes.doorsForDisableOrEnableWithUnits
                .filter((door) => !!door && !door.in_assortment)
                .map(({ name }) => name),
        [changes.doorsForDisableOrEnableWithUnits]
    );

    const hasDataToChange = useMemo(() => {
        const { columnsToCreate, columnsToUpdate, doorsForDisableOrEnable, doorsForDisableOrEnableWithUnits } = changes;

        return (
            !!columnsToCreate.length ||
            !!columnsToUpdate.length ||
            !!doorsForDisableOrEnable.length ||
            !!doorsForDisableOrEnableWithUnits.length
        );
    }, [changes]);

    const handleCancelChanges = useCallback(() => {
        handleClearValueStates();
        handleToggleImportDoorAttributesDialog();
        setIsOpenDialogToConfirmChanges(false);
    }, [handleToggleImportDoorAttributesDialog]);

    const handleClickOk = useCallback(() => {
        handleClearValueStates();
        handleToggleManageAllocationDimension();
        setIsOpenDialogToConfirmChanges(false);
    }, [handleToggleManageAllocationDimension]);

    const handleOpenConfirmDialog = useCallback(() => {
        setIsOpenDialogToConfirmChanges(true);
    }, []);

    const handleClearErrors = () => {
        dispatch({ type: UploadActionKind.ClearErrorMessages });
    };

    const contextValue = useMemo(
        () => ({
            handleDrop,
            handleCancelChanges,
            handleConfirmChanges,
            handleOpenConfirmDialog,
            handleClickOk,
            clearValueStates: handleClearValueStates,
            handleClearErrors,

            errorMessages: errors.messages,
            isUploading: feedbackUpload.isUploading,
            wasProcessed: feedbackUpload.wasProcessed,

            wasUpdated: feedbackUpdatingData.wasUpdated,
            isUpdating: feedbackUpdatingData.isUpdating,

            isOpenDialogToConfirmChanges,
            hasDataToChange,

            columnsToCreate: changes.columnsToCreate,
            columnsToUpdate: changes.columnsToUpdate,
            columnsHeadersMap: changes.columnsHeadersMap,
            doorsForDisableOrEnable: changes.doorsForDisableOrEnable.map(({ name }) => name),
            doorsForEnableWithUnits,
            doorsForDisableWithUnits,
        }),
        [
            changes,
            doorsForDisableWithUnits,
            doorsForEnableWithUnits,
            errors.messages,
            feedbackUpdatingData.isUpdating,
            feedbackUpdatingData.wasUpdated,
            feedbackUpload.isUploading,
            feedbackUpload.wasProcessed,
            handleCancelChanges,
            handleClickOk,
            handleConfirmChanges,
            handleDrop,
            handleOpenConfirmDialog,
            hasDataToChange,
            isOpenDialogToConfirmChanges,
        ]
    );

    return <UploaderContext.Provider value={contextValue}>{children}</UploaderContext.Provider>;
};

const mapStateToProps = (store: IStore): IReduxProps => ({
    merchantSharedSettings: store.merchantDefaultSharedSettings.result ?? {},
    mutate: store.mutate,
});

export const UploaderProvider = connect<IReduxProps, unknown, IUploaderProviderProps>(mapStateToProps)(
    BaseUploaderProvider
);
